'use client';

/**
 * TanStack Query hooks for Messaging Conversations
 *
 * Features:
 * - Fetch conversations with filters
 * - Optimistic updates for instant UI feedback
 * - Automatic cache invalidation
 * - Realtime-ready (integrates with useRealtimeSyncMessaging)
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { queryKeys } from '../index';
import { supabase } from '@/lib/supabase';
import { sanitizePostgrestValue } from '@/lib/utils/sanitize';
import { useAuth } from '@/context/AuthContext';
import type {
  DbMessagingConversation,
  MessagingConversation,
  ConversationView,
  ConversationFilters,
  UpdateConversationInput,
} from '@/lib/messaging/types';
import {
  transformConversation as transform,
  isWindowExpired as checkWindowExpired,
  getWindowMinutesRemaining as getWindowMinutes,
} from '@/lib/messaging/types';

// =============================================================================
// PENDING DELETION GUARD
// =============================================================================

/**
 * Module-level set of conversation IDs currently being deleted.
 *
 * Problem: Other mutations (e.g. markAsRead) have their own onSettled that calls
 * invalidateQueries(messagingConversations.all). If they complete while a delete
 * is in-flight, their refetch can return the conversation still in the DB
 * (the DELETE hasn't committed yet), overwriting the optimistic removal.
 *
 * Solution: addPendingDeletion() before starting the delete. The useConversations
 * `select` filter removes the ID from every query result until removePendingDeletion()
 * is called (in onSettled, after the mutation settles either way).
 */
export const pendingDeletionIds = new Set<string>();

export function addPendingDeletion(id: string): void {
  pendingDeletionIds.add(id);
}

export function removePendingDeletion(id: string): void {
  pendingDeletionIds.delete(id);
}

// =============================================================================
// PENDING ACKNOWLEDGE GUARD
// =============================================================================

/**
 * Same pattern as pendingDeletionIds but for the "dismiss AGUARDANDO" action.
 *
 * Problem: clicking X fires an optimistic update (pendingAcknowledgedAt = now) +
 * a background POST to /acknowledge. Any realtime event that arrives before the
 * POST commits triggers invalidateQueries → refetch → server returns the
 * conversation still as isPending=true → overwrites the optimistic update →
 * conversation flickers back then disappears again after the POST finally commits.
 *
 * Solution: add the id here before the POST, remove it in .then()/.catch().
 * The realtime handler skips invalidation for ids in this set (same guard as
 * pendingDeletionIds in useRealtimeSync.ts).
 */
export const pendingAcknowledgedIds = new Set<string>();

export function addPendingAcknowledge(id: string): void {
  pendingAcknowledgedIds.add(id);
}

export function removePendingAcknowledge(id: string): void {
  pendingAcknowledgedIds.delete(id);
}

// =============================================================================
// QUERY HOOKS
// =============================================================================

/**
 * Fetch all conversations with optional filters.
 * Returns ConversationView[] with denormalized channel and contact data.
 */
export function useConversations(filters?: ConversationFilters, limit?: number) {
  const { user, profile, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: [...queryKeys.messagingConversations.filtered(filters), limit ?? 'all'],
    queryFn: async (): Promise<ConversationView[]> => {
      // Build query with joins for denormalized data
      let query = supabase
        .from('messaging_conversations')
        .select(`
          *,
          channel:messaging_channels!channel_id (
            id,
            name,
            channel_type,
            provider
          ),
          contact:contacts!contact_id (
            id,
            name,
            email,
            phone,
            classification,
            stage,
            ai_paused,
            is_blocked,
            is_ad_lead,
            ad_name
          ),
          assigned_user:profiles!assigned_user_id (
            id,
            name,
            avatar_url
          ),
          reactivation_prev_deal:deals!reactivation_from_deal_id (
            id,
            title,
            is_won,
            is_lost
          )
        `)
        .order('last_message_at', { ascending: false, nullsFirst: false });

      // Defense-in-depth: filter by organization even though RLS handles it
      if (profile?.organization_id) {
        query = query.eq('organization_id', profile.organization_id);
      }

      // Apply filters
      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      // channelId = specific channel UUID (e.g. Klaus's WhatsApp vs Nicole's WhatsApp)
      if (filters?.channelId) {
        query = query.eq('channel_id', filters.channelId);
      }
      if (filters?.businessUnitId) {
        query = query.eq('business_unit_id', filters.businessUnitId);
      }
      if (filters?.assignedUserId === 'unassigned') {
        query = query.is('assigned_user_id', null);
      } else if (filters?.assignedUserId) {
        query = query.eq('assigned_user_id', filters.assignedUserId);
      }
      if (filters?.hasUnread) {
        query = query.gt('unread_count', 0);
      }
      // Server-side inbound-only filter for Aguardando mode — dramatically reduces payload
      // vs fetching all open conversations and filtering client-side.
      if (filters?.pendingOnly) {
        query = query.eq('last_message_direction', 'inbound');
      }
      // Server-side search: nome, telefone, preview. ad_name fica client-side (campo joined).
      if (filters?.search) {
        const safe = sanitizePostgrestValue(filters.search);
        if (safe) {
          query = query.or(
            `external_contact_name.ilike.%${safe}%,external_contact_id.ilike.%${safe}%,last_message_preview.ilike.%${safe}%`
          );
        }
      }
      // Sempre seta um limit explícito — sem ele PostgREST aplica o default
      // do projeto (normalmente 1000), cortando conversas antigas.
      query = query.limit(limit ?? 5000);

      const { data, error } = await query;
      if (error) throw error;

      // Transform to ConversationView, then apply client-side channelType filter
      // (channel_type lives on the joined messaging_channels row, not on conversations)
      const rows = (data || []).filter((row) => {
        if (!filters?.channelType) return true;
        const ch = row.channel as { channel_type?: string } | null;
        return ch?.channel_type === filters.channelType;
      });

      return rows.map((row): ConversationView => {
        const base = transform(row as DbMessagingConversation);
        const channel = row.channel as { id: string; name: string; channel_type: string; provider: string } | null;
        // Supabase pode devolver o objeto joined como array OU objeto dependendo da inferência.
        const rawContact = row.contact as any;
        const contact = (Array.isArray(rawContact) ? rawContact[0] : rawContact) as
          | { id?: string; name?: string; email?: string; phone?: string; classification?: string; stage?: string; ai_paused?: boolean; is_blocked?: boolean; is_ad_lead?: boolean; ad_name?: string | null }
          | null;
        const assignedUser = row.assigned_user as { id: string; name: string; avatar_url: string } | null;
        const rawPrev = (row as any).reactivation_prev_deal;
        const prevDeal = (Array.isArray(rawPrev) ? rawPrev[0] : rawPrev) as
          | { id?: string; title?: string; is_won?: boolean; is_lost?: boolean }
          | null;

        return {
          ...base,
          channelType: (channel?.channel_type || 'whatsapp') as ConversationView['channelType'],
          channelName: channel?.name || 'Canal',
          contactName: contact?.name,
          contactEmail: contact?.email,
          contactPhone: contact?.phone,
          contactClassification: contact?.classification,
          contactStage: contact?.stage,
          contactAiPaused: contact?.ai_paused ?? false,
          contactIsBlocked: contact?.is_blocked ?? false,
          contactIsAdLead: contact?.is_ad_lead ?? false,
          contactAdName: contact?.ad_name ?? undefined,
          assignedUserName: assignedUser?.name,
          assignedUserAvatar: assignedUser?.avatar_url,
          isWindowExpired: checkWindowExpired(base, channel?.provider),
          windowMinutesRemaining: getWindowMinutes(base, channel?.provider),
          reactivationPreviousDealState: prevDeal?.is_won ? 'won' : prevDeal?.is_lost ? 'lost' : undefined,
          reactivationPreviousDealTitle: prevDeal?.title ?? undefined,
        };
      });
    },
    staleTime: 2 * 60 * 1000, // 2 min — realtime subscription handles freshness
    enabled: !authLoading && !!user && !!profile?.organization_id,
    placeholderData: keepPreviousData,
    // Filter out conversations being deleted so stale refetches from other
    // mutations (e.g. markAsRead.onSettled) can't re-add them while the
    // delete mutation is in-flight.
    select: (data) =>
      pendingDeletionIds.size === 0
        ? data
        : data.filter((conv) => !pendingDeletionIds.has(conv.id)),
  });
}

/**
 * Fetch a single conversation by ID.
 */
export function useConversation(conversationId: string | undefined) {
  const { user, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: queryKeys.messagingConversations.detail(conversationId || ''),
    queryFn: async (): Promise<ConversationView | null> => {
      const { data, error } = await supabase
        .from('messaging_conversations')
        .select(`
          *,
          channel:messaging_channels!channel_id (
            id,
            name,
            channel_type,
            provider
          ),
          contact:contacts!contact_id (
            id,
            name,
            email,
            phone
          ),
          assigned_user:profiles!assigned_user_id (
            id,
            name,
            avatar_url
          ),
          reactivation_prev_deal:deals!reactivation_from_deal_id (
            id,
            title,
            is_won,
            is_lost
          )
        `)
        .eq('id', conversationId!)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      const base = transform(data as DbMessagingConversation);
      const channel = data.channel as { id: string; name: string; channel_type: string; provider: string } | null;
      const contact = data.contact as { id: string; name: string; email: string; phone: string } | null;
      const assignedUser = data.assigned_user as { id: string; name: string; avatar_url: string } | null;
      const rawPrev = (data as any).reactivation_prev_deal;
      const prevDeal = (Array.isArray(rawPrev) ? rawPrev[0] : rawPrev) as
        | { id?: string; title?: string; is_won?: boolean; is_lost?: boolean }
        | null;

      return {
        ...base,
        channelType: (channel?.channel_type || 'whatsapp') as ConversationView['channelType'],
        channelName: channel?.name || 'Canal',
        contactName: contact?.name,
        contactEmail: contact?.email,
        contactPhone: contact?.phone,
        assignedUserName: assignedUser?.name,
        assignedUserAvatar: assignedUser?.avatar_url,
        isWindowExpired: checkWindowExpired(base, channel?.provider),
        windowMinutesRemaining: getWindowMinutes(base, channel?.provider),
        reactivationPreviousDealState: prevDeal?.is_won ? 'won' : prevDeal?.is_lost ? 'lost' : undefined,
        reactivationPreviousDealTitle: prevDeal?.title ?? undefined,
      };
    },
    staleTime: 30 * 1000,
    enabled: !authLoading && !!user && !!conversationId,
  });
}

/**
 * Fetch the most recent conversation for a contact (used by DealDetailModal
 * to show the WhatsApp thread inline). Returns null if the contact has no
 * conversation yet.
 */
export function useConversationByContact(contactId: string | undefined | null) {
  const { user, profile, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: ['messagingConversations', 'byContact', contactId || ''],
    queryFn: async (): Promise<ConversationView | null> => {
      if (!contactId) return null;
      const { data, error } = await supabase
        .from('messaging_conversations')
        .select(`
          *,
          channel:messaging_channels!channel_id (
            id,
            name,
            channel_type,
            provider
          ),
          contact:contacts!contact_id (
            id,
            name,
            email,
            phone
          ),
          assigned_user:profiles!assigned_user_id (
            id,
            name,
            avatar_url
          ),
          reactivation_prev_deal:deals!reactivation_from_deal_id (
            id,
            title,
            is_won,
            is_lost
          )
        `)
        .eq('contact_id', contactId)
        .eq('organization_id', profile?.organization_id ?? '')
        .order('last_message_at', { ascending: false, nullsFirst: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      const base = transform(data as DbMessagingConversation);
      const channel = data.channel as { id: string; name: string; channel_type: string; provider: string } | null;
      const contact = data.contact as { id: string; name: string; email: string; phone: string } | null;
      const assignedUser = data.assigned_user as { id: string; name: string; avatar_url: string } | null;
      const rawPrev = (data as any).reactivation_prev_deal;
      const prevDeal = (Array.isArray(rawPrev) ? rawPrev[0] : rawPrev) as
        | { id?: string; title?: string; is_won?: boolean; is_lost?: boolean }
        | null;

      return {
        ...base,
        channelType: (channel?.channel_type || 'whatsapp') as ConversationView['channelType'],
        channelName: channel?.name || 'Canal',
        contactName: contact?.name,
        contactEmail: contact?.email,
        contactPhone: contact?.phone,
        assignedUserName: assignedUser?.name,
        assignedUserAvatar: assignedUser?.avatar_url,
        isWindowExpired: checkWindowExpired(base, channel?.provider),
        windowMinutesRemaining: getWindowMinutes(base, channel?.provider),
        reactivationPreviousDealState: prevDeal?.is_won ? 'won' : prevDeal?.is_lost ? 'lost' : undefined,
        reactivationPreviousDealTitle: prevDeal?.title ?? undefined,
      };
    },
    staleTime: 30 * 1000,
    enabled: !authLoading && !!user && !!contactId && !!profile?.organization_id,
  });
}

/**
 * Fetch the active (open) deal for a contact, if any. Reads from
 * `vw_contact_active_deal` which already filters won/lost/deleted and
 * orders by created_at desc.
 */
export function useActiveDealForContact(contactId: string | undefined | null) {
  const { user, profile, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: ['activeDeal', 'byContact', contactId || ''],
    queryFn: async (): Promise<{
      deal_id: string;
      reactivation_sequence?: number;
      reactivated_from_deal_id?: string | null;
    } | null> => {
      if (!contactId) return null;
      const { data, error } = await supabase
        .from('vw_contact_active_deal')
        .select('deal_id, reactivation_sequence, reactivated_from_deal_id')
        .eq('contact_id', contactId)
        .eq('organization_id', profile?.organization_id ?? '')
        .maybeSingle();
      if (error) return null;
      return data;
    },
    staleTime: 60 * 1000,
    enabled: !authLoading && !!user && !!contactId && !!profile?.organization_id,
  });
}

/**
 * Fetch the set of contact_ids that have an active deal.
 * Used by the messaging list to classify conversations as Bot vs Humano.
 */
export function useContactsWithActiveDeal() {
  const { user, profile, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: ['activeDeal', 'contactIds', profile?.organization_id ?? ''],
    queryFn: async (): Promise<Set<string>> => {
      try {
        const { data, error } = await supabase
          .from('vw_contact_active_deal')
          .select('contact_id')
          .eq('organization_id', profile?.organization_id ?? '');
        if (error) return new Set();
        return new Set((data || []).map((r: any) => r.contact_id as string));
      } catch (error) {
        console.warn('[useContactsWithActiveDeal] Failed to fetch active deals:', error);
        return new Set();
      }
    },
    staleTime: 30 * 1000,
    enabled: !authLoading && !!user && !!profile?.organization_id,
  });
}

/**
 * Fetch a Map of contact_id → stage mode for active deals.
 * Used by the messaging list to classify conversations as Bot vs Humano based on the stage mode.
 */
export function useContactStageModes() {
  const { user, profile, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: ['activeDeal', 'contactStageModes', profile?.organization_id ?? ''],
    queryFn: async (): Promise<Map<string, string>> => {
      const orgId = profile?.organization_id;
      if (!orgId) return new Map();
      try {
        const { data: deals, error: dealsError } = await supabase
          .from('vw_contact_active_deal')
          .select('contact_id, stage_id')
          .eq('organization_id', orgId);

        if (dealsError || !deals || deals.length === 0) return new Map();

        const stageIds = Array.from(new Set(deals.map((d: any) => d.stage_id).filter(Boolean)));
        if (stageIds.length === 0) return new Map();

        const { data: stages, error: stagesError } = await supabase
          .from('board_stages')
          .select('id, mode')
          .in('id', stageIds);

        if (stagesError) return new Map();

        const stageModeMap = new Map<string, string>();
        (stages || []).forEach((s: any) => stageModeMap.set(s.id, s.mode || 'humano'));

        const result = new Map<string, string>();
        for (const d of deals) {
          if (d.contact_id && d.stage_id) {
            result.set(d.contact_id as string, stageModeMap.get(d.stage_id as string) || 'humano');
          }
        }
        return result;
      } catch (error) {
        console.warn('[useContactStageModes] Failed to fetch stage modes:', error);
        return new Map();
      }
    },
    staleTime: 30 * 1000,
    enabled: !authLoading && !!user && !!profile?.organization_id,
  });
}

/**
 * Fetch a Map of contact_id → tags[] for contacts in the org.
 * Used by the messaging list to filter conversations by tag.
 */
export function useContactTags() {
  const { user, profile, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: ['contacts', 'tags', profile?.organization_id ?? ''],
    queryFn: async (): Promise<Map<string, string[]>> => {
      const orgId = profile?.organization_id;
      if (!orgId) return new Map();

      const result = new Map<string, string[]>();
      // Paginação: o PostgREST limita a 1000 linhas por request. Orgs grandes
      // (ex: Tiago com 2300+ contatos com tags) tinham contatos cortados do mapa,
      // fazendo o filtro por tag esconder deals que DEVERIAM aparecer (o card
      // mostrava o pill mas o filtro sumia com ele). Buscamos em páginas de 1000
      // até esgotar.
      const PAGE = 1000;
      for (let from = 0; ; from += PAGE) {
        const { data: contacts, error } = await supabase
          .from('contacts')
          .select('id, tags')
          .eq('organization_id', orgId)
          .is('deleted_at', null)
          .not('tags', 'is', null)
          .order('id', { ascending: true })
          .range(from, from + PAGE - 1);
        if (error) break;
        for (const c of contacts ?? []) {
          if (c.id && Array.isArray(c.tags) && c.tags.length > 0) {
            result.set(c.id as string, c.tags as string[]);
          }
        }
        if (!contacts || contacts.length < PAGE) break; // última página
      }
      return result;
    },
    staleTime: 60 * 1000,
    enabled: !authLoading && !!user && !!profile?.organization_id,
  });
}

/**
 * Fetch Set<contact_id> for contacts that have an active (non-won/non-lost) deal
 * in any of the given stage IDs.
 *
 * Query key inclui os stageIds → refetch automático quando o filtro muda.
 * Abordagem pontual: em vez de carregar TODOS os deals da org em um mapa global
 * (que sofre com limites do PostgREST), busca só os deals dos stages selecionados.
 */
export function useContactsInStage(stageIds: Set<string>) {
  const { user, profile, loading: authLoading } = useAuth();
  const sorted = Array.from(stageIds).sort();

  return useQuery({
    queryKey: ['contactsInStage', profile?.organization_id ?? '', sorted.join(',')],
    queryFn: async (): Promise<Set<string>> => {
      const orgId = profile?.organization_id;
      if (!orgId || sorted.length === 0) return new Set();

      // Deals ativos no stage selecionado
      const { data: active } = await supabase
        .from('deals')
        .select('contact_id')
        .eq('organization_id', orgId)
        .in('stage_id', sorted)
        .is('deleted_at', null)
        .eq('is_won', false)
        .eq('is_lost', false);

      // Deals ganhos/perdidos no stage selecionado (para filtros de stages terminais como "perdido"/"ganho")
      const { data: closed } = await supabase
        .from('deals')
        .select('contact_id')
        .eq('organization_id', orgId)
        .in('stage_id', sorted)
        .is('deleted_at', null)
        .or('is_won.eq.true,is_lost.eq.true');

      // Todos os contact_ids com qualquer deal ativo na org (qualquer stage) —
      // usado para excluir contatos reativados dos resultados de stages terminais
      const { data: anyActive } = await supabase
        .from('deals')
        .select('contact_id')
        .eq('organization_id', orgId)
        .is('deleted_at', null)
        .eq('is_won', false)
        .eq('is_lost', false)
        .limit(5000);

      const anyActiveIds = new Set((anyActive ?? []).map((d) => d.contact_id).filter(Boolean));
      const result = new Set<string>();
      for (const d of active ?? []) {
        if (d.contact_id) result.add(d.contact_id as string);
      }
      // Inclui contatos de deals fechados apenas se não têm nenhum deal ativo —
      // evita que contatos reativados apareçam em filtros de stages terminais (ex: "perdido")
      for (const d of closed ?? []) {
        if (d.contact_id && !anyActiveIds.has(d.contact_id)) result.add(d.contact_id as string);
      }
      return result;
    },
    staleTime: 30 * 1000,
    enabled: !authLoading && !!user && !!profile?.organization_id && stageIds.size > 0,
  });
}

/**
 * @deprecated Use useContactsInStage instead.
 * Mantido para compatibilidade com useRealtimeSync que invalida esta query key.
 */
export function useContactStageIds() {
  const { user, profile, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: ['allDeal', 'contactStageIds', profile?.organization_id ?? ''],
    queryFn: async (): Promise<Map<string, string>> => new Map(),
    staleTime: Infinity,
    enabled: false,
  });
}

/**
 * Fetch unread conversations count.
 */
export function useUnreadCount() {
  const { user, loading: authLoading } = useAuth();

  return useQuery({
    queryKey: queryKeys.messagingConversations.unreadCount(),
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from('messaging_conversations')
        .select('*', { count: 'exact', head: true })
        .gt('unread_count', 0)
        .eq('status', 'open');

      if (error) throw error;
      return count || 0;
    },
    staleTime: 60 * 1000, // 60s - realtime subscription handles live updates
    refetchOnWindowFocus: false,
    enabled: !authLoading && !!user,
  });
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Update a conversation (status, priority, assignment).
 */
export function useUpdateConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      updates,
    }: {
      conversationId: string;
      updates: UpdateConversationInput;
    }) => {
      // Transform to snake_case for DB
      const dbUpdates: Record<string, unknown> = {};
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.priority !== undefined) dbUpdates.priority = updates.priority;
      if (updates.assignedUserId !== undefined) {
        dbUpdates.assigned_user_id = updates.assignedUserId;
        dbUpdates.assigned_at = updates.assignedUserId ? new Date().toISOString() : null;
      }

      const { error } = await supabase
        .from('messaging_conversations')
        .update(dbUpdates)
        .eq('id', conversationId);

      if (error) throw error;
      return { conversationId, updates };
    },
    onMutate: async ({ conversationId, updates }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.messagingConversations.all,
      });

      // Snapshot previous value
      const previousConversations = queryClient.getQueriesData({
        queryKey: queryKeys.messagingConversations.all,
      });

      // Optimistically update list caches.
      // Guard against non-array entries (e.g. detail queries return ConversationView | null).
      queryClient.setQueriesData(
        { queryKey: queryKeys.messagingConversations.all },
        (old: unknown) => {
          if (!Array.isArray(old)) return old;
          return (old as ConversationView[]).map((conv) =>
            conv.id === conversationId ? { ...conv, ...updates } : conv
          );
        }
      );

      // Also optimistically update the detail query so the header dropdown
      // reflects the change immediately without waiting for the refetch.
      queryClient.setQueryData(
        queryKeys.messagingConversations.detail(conversationId),
        (old: ConversationView | null | undefined) => {
          if (!old) return old;
          return { ...old, ...updates };
        }
      );

      return { previousConversations };
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previousConversations) {
        for (const [queryKey, data] of context.previousConversations) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.messagingConversations.all,
      });
    },
  });
}

/**
 * Mark conversation as read (reset unread count).
 */
export function useMarkConversationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (conversationId: string) => {
      const { error } = await supabase
        .from('messaging_conversations')
        .update({ unread_count: 0 })
        .eq('id', conversationId);

      if (error) throw error;
      return conversationId;
    },
    onMutate: async (conversationId) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.messagingConversations.all,
      });

      // Optimistically set unread to 0 in list caches
      // Guard against non-array entries (detail queries return ConversationView | null)
      queryClient.setQueriesData(
        { queryKey: queryKeys.messagingConversations.all },
        (old: unknown) => {
          if (!Array.isArray(old)) return old;
          return (old as ConversationView[]).map((conv) =>
            conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv
          );
        }
      );

      // Also update the detail query so the useEffect in MessagingPage stops re-firing
      queryClient.setQueryData(
        queryKeys.messagingConversations.detail(conversationId),
        (old: ConversationView | null | undefined) => {
          if (!old) return old;
          return { ...old, unreadCount: 0 };
        }
      );
    },
    onSettled: (_data, _error, conversationId) => {
      // Skip conversations-list invalidation while a delete is in-progress.
      // The delete mutation handles cache cleanup directly, and invalidating here
      // triggers a refetch that can return the conversation from DB before it's
      // fully deleted, causing it to flash back into the list.
      if (pendingDeletionIds.size === 0) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.messagingConversations.all,
        });
      }
      // Invalidate detail query to sync with DB
      queryClient.invalidateQueries({
        queryKey: queryKeys.messagingConversations.detail(conversationId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.messagingConversations.unreadCount(),
      });
    },
  });
}

/**
 * Resolve (close) a conversation.
 */
export function useResolveConversation() {
  const updateMutation = useUpdateConversation();

  return useMutation({
    mutationFn: async (conversationId: string) => {
      return updateMutation.mutateAsync({
        conversationId,
        updates: { status: 'resolved' },
      });
    },
  });
}

/**
 * Reopen a conversation.
 */
export function useReopenConversation() {
  const updateMutation = useUpdateConversation();

  return useMutation({
    mutationFn: async (conversationId: string) => {
      return updateMutation.mutateAsync({
        conversationId,
        updates: { status: 'open' },
      });
    },
  });
}

/**
 * Assign conversation to a user.
 */
export function useAssignConversation() {
  const updateMutation = useUpdateConversation();

  return useMutation({
    mutationFn: async ({
      conversationId,
      userId,
    }: {
      conversationId: string;
      userId: string | null;
    }) => {
      return updateMutation.mutateAsync({
        conversationId,
        updates: { assignedUserId: userId },
      });
    },
  });
}

/**
 * Delete a conversation and all its messages.
 * Use with caution - this is a destructive action.
 */
export function useDeleteConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (conversationId: string) => {
      // Server-side delete handles all FK dependents (ai_conversation_log,
      // pipeline_events, message_processing_jobs, bot_sent_messages, etc.)
      // using the service-role client to bypass RLS.
      const res = await fetch(`/api/messaging/conversations/${conversationId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Erro ${res.status}`);
      }
      return conversationId;
    },
    onSuccess: (deletedId) => {
      // Cancel any in-flight refetches triggered by realtime during the mutation.
      // The messaging_messages DELETE event fires before the conversation is deleted,
      // causing a refetch that returns the conversation still in the list and overwrites
      // the optimistic removal. Cancelling here prevents that race condition.
      queryClient.cancelQueries({ queryKey: queryKeys.messagingConversations.all });

      // Remove detail query so onSettled invalidateQueries doesn't refetch it
      queryClient.removeQueries({
        queryKey: queryKeys.messagingConversations.detail(deletedId),
      });
      // Remove from list caches — guard against non-array entries (e.g. detail queries)
      queryClient.setQueriesData(
        { queryKey: queryKeys.messagingConversations.all },
        (old: unknown) => {
          if (!Array.isArray(old)) return old;
          return (old as ConversationView[]).filter((conv) => conv.id !== deletedId);
        }
      );
    },
    onSettled: () => {
      // NOTE: removePendingDeletion is intentionally NOT called here.
      // The guard must stay up until the messaging_conversations DELETE realtime event
      // arrives (in useRealtimeSync). The DB trigger that runs on messages DELETE fires a
      // conversations UPDATE event that arrives via WebSocket AFTER this HTTP onSettled —
      // if we lower the guard here, that UPDATE queues a refetch that re-shows the deleted
      // conversation before the DELETE realtime event cleans it up.
      //
      // NOTE: invalidateQueries(messagingConversations.all) is intentionally omitted.
      // onSuccess already removed the conversation from cache; an extra invalidation here
      // races with the still-pending DB trigger and can re-fetch a stale row.
      queryClient.invalidateQueries({
        queryKey: queryKeys.messagingConversations.unreadCount(),
      });
    },
  });
}
